'use strict';

const { getTranslation } = require('../common/jsTranslations');
const { getConfig } = require('../common/jsConfig');

const VALIDATION_IGNORE_INPUT_TYPES = ['button', 'submit', 'reset'];
const CUSTOM_VALIDATION_IGNORE_INPUT_TYPES = ['checkbox', 'range', 'file', 'hidden', 'radio'];

const ZIPCODE_ATTRIBUTE_IS_UPPERCASE = 'data-is-uppercase';

/**
 * Returns allowed alphabets.
 * Hint: calculates only once on 1st call (useful when called after each input)
 *
 * @return {Array} - array of allowed alphabets
 */
const getAllowedAlphabets = (() => {
    let allowedAlphabets;
    return () => {
        if (!allowedAlphabets) {
            try {
                const validationAllowedAlphabets = getConfig('validationAllowedAlphabets');
                allowedAlphabets = JSON.parse(validationAllowedAlphabets);
            } catch (e) {
                console.error('Error parsing alphabetValidationSettings json.');
                allowedAlphabets = [];
            }
        }
        return allowedAlphabets;
    };
})();

function getValidationIgnoreInputTypesSelector() {
    return VALIDATION_IGNORE_INPUT_TYPES.map(type => `[type="${type}"]`).join(',');
}

function getCustomValidationMsg($field) {
    if (!$field) {
        return '';
    }

    const fieldValue = $field.val();

    /*
        note:
        phpstorm has bug and might mark line below ("Extended_Pictographic") with "Unknown character category" error.
        https://youtrack.jetbrains.com/issue/IJPL-101766/Unknown-character-category-on-ExtendedPictographic-RegExp-Unicode-property-escape

        If phpstorm bug is resolved and it's not case anymore, please remove this comment.
    */
    const emojiRegEx = /\p{Extended_Pictographic}/gu;
    if (emojiRegEx.test(fieldValue)) {
        return getTranslation('error.message.emoji');
    }

    const allowedAlphabets = getAllowedAlphabets();
    if (!allowedAlphabets || allowedAlphabets.length === 0) {
        console.warn('Field validation, allowedAlphabets is missing (alphabet validation is skipped).');
    } else {
        const alphabetPatterns = {
            latin: /\p{Script=Latin}/u,
            chinese: /\p{Script=Han}/u,
            japanese: /[\u3000-\u303f\u3040-\u309f\u30a0-\u30ff\uff00-\uff9f\u4e00-\u9faf\u3400-\u4dbf]/,
            korean: /[\uac00-\ud7af]|[\u1100-\u11ff]|[\u3130-\u318f]|[\ua960-\ua97f]|[\ud7b0-\ud7ff]/,
            arabic: /\p{sc=Arabic}/u,
            devanagari: /[\u0900-\u097F]/,
            cyrillic: /\p{sc=Cyrillic}/u,
            greek: /\p{sc=Greek}/u,
            ethiopian: /\p{sc=Ethiopic}/u,
            hebrew: /\p{sc=Hebrew}/u,
            armenian: /\p{sc=Armenian}/u,
            georgian: /\p{sc=Georgian}/u
        };

        let invalidAlphabetKey = '';
        Object.keys(alphabetPatterns).forEach((alphabetKey) => {
            if (allowedAlphabets.includes(alphabetKey)) {
                return;
            }

            if (alphabetPatterns[alphabetKey].test(fieldValue)) {
                invalidAlphabetKey = alphabetKey;
            }
        });

        if (invalidAlphabetKey) {
            return getTranslation(`error.message.alphabet.not.allowed.${invalidAlphabetKey}`);
        }
    }

    const maxLength = $field.attr('maxlength');
    if (maxLength > 0 && fieldValue.length > maxLength) {
        return getTranslation('error.message.date.range.custom.maxlength')
            .replace('{0}', maxLength);
    }

    if ($field.attr('data-skip-whitespace-validation') !== 'true') {
        const invalidWhitespaceRegEx = /(^\s)|(\s{2,})|(\s$)/;
        if (invalidWhitespaceRegEx.test(fieldValue)) {
            return getTranslation('error.message.invalid.whitespace');
        }
    }

    if ($field.attr('data-skip-invalid-chars-validation') !== 'true') {
        const defaultInvalidChars = getConfig('formValidationInvalidCharsDefault');
        if (!defaultInvalidChars) {
            console.warn('Field validation, formValidationInvalidCharsDefault is missing.');
        }
        let overrideInvalidChars = $field.attr('data-override-invalid-chars');
        if (overrideInvalidChars === 'null') {
            overrideInvalidChars = '';
        }
        const invalidChars = overrideInvalidChars || defaultInvalidChars;
        if (invalidChars) {
            const invalidCharsRegExp = new RegExp(`[${invalidChars}]`);
            if (invalidCharsRegExp.test(fieldValue)) {
                return getTranslation('error.message.invalid.chars')
                    .replace('{invalidCharsPlaceholder}', invalidChars);
            }
        }
    }

    return '';
}

function passedCustomValidation($field) {
    const errorMsg = getCustomValidationMsg($field);
    if (errorMsg) {
        $field.addClass('is-invalid');
        $field.siblings().filter('.invalid-feedback').html(errorMsg);
    }

    return errorMsg.length === 0;
}

function scrollToFirstInvalidField(form) {
    if (!form) {
        return;
    }

    const firstInvalidField = $(form).find('.is-invalid:visible:first');
    if (firstInvalidField.length) {
        firstInvalidField.get(0).scrollIntoView({
            behavior: 'smooth',
            block: 'center'
        });
    }
}

/**
 * Checks if field is valid.
 * Runs both browser native validation and our custom validation.
 *
 * Note: If both checks are invalid, last called will render error msg.
 *
 * @param {Element|null} field - field to validate
 * @return {boolean} - true if field is valid
 */
function validateField(field) {
    if (typeof field === 'undefined') {
        return false;
    }

    if (!field) {
        console.error('Field validation error - no field.');
        return false;
    }

    const fieldType = field.type;
    if (VALIDATION_IGNORE_INPUT_TYPES.includes(fieldType)) {
        console.warn('Field validation run on ignored types.');
        return true;
    }

    field.classList.remove('is-invalid');
    const passedNativeValidation = field.reportValidity();

    const fieldTagName = field.tagName.toLowerCase();
    const shouldSkipCustomValidation = (fieldTagName === 'input' && CUSTOM_VALIDATION_IGNORE_INPUT_TYPES.includes(field.type))
        || fieldTagName === 'select';

    if (shouldSkipCustomValidation) {
        return passedNativeValidation;
    }

    return passedCustomValidation($(field)) && passedNativeValidation;
}

/**
 * Remove all validation. Should be called every time before revalidating form
 * @param {element} form - Form to be cleared
 * @returns {void}
 */
function clearForm(form) {
    $(form).find('.is-invalid').removeClass('is-invalid');
}

/**
 * Validate whole form. Requires `this` to be set to form object
 * @param {jQuery.event} event - Event to be canceled if form is invalid.
 * @param {string} [excludeSelector] - Fields inside elements matching this selector are not validated
 * @returns {boolean} - Flag to indicate if form is valid
 * @important If excludeSelector is set, form level reportValidity() is skipped since it would also
 * check the excluded fields. Native validation still runs per field inside validateField().
 * excludeSelector is resolved via jQuery, so pseudo selectors like :hidden are supported.
 */
function validateForm(event, excludeSelector) {
    if ($(this).is('form')) {
        clearForm(this);
    }
    let formPassedNativeValidation = $(this).is('form')
        && typeof this.reportValidity === 'function'
        && (excludeSelector ? true : this.reportValidity());

    let areFieldsValid = true;
    let fields = $(this).find(`input:not(${getValidationIgnoreInputTypesSelector()}), textarea, select`);
    if (excludeSelector) {
        fields = fields.not($(this).find(excludeSelector).find('input, textarea, select'));
    }
    for (let i = 0; i < fields.length; i++) {
        if (!validateField(fields[i])) {
            areFieldsValid = false;
        }
    }

    const isValid = formPassedNativeValidation && areFieldsValid;

    if (!isValid) {
        scrollToFirstInvalidField(this);

        if (event) {
            event.preventDefault();
            event.stopImmediatePropagation(); // idea: form invalid => don't execute other submit events (ajax call)
        }
    }

    return isValid;
}

function transformToUpperCaseAndKeepCursor($field) {
    if ($field && $field.length) {
        const field = $field[0];
        const cursorInitialPosition = field.selectionStart;

        field.value = field.value.toUpperCase();
        field.selectionEnd = cursorInitialPosition;
    }
}

function handleZipCodeFieldUppercase($zipCodeField, zipCodeRule) {
    let isZipCodeUppercase = zipCodeRule?.isUppercase || false;
    $zipCodeField.attr(ZIPCODE_ATTRIBUTE_IS_UPPERCASE, isZipCodeUppercase);
    if (isZipCodeUppercase) {
        transformToUpperCaseAndKeepCursor($zipCodeField);
    }
}

module.exports = {
    invalid: function () {
        $('form input, form select').on('invalid', function (e) {
            e.preventDefault();
            this.setCustomValidity('');
            if (!this.validity.valid) {
                var validationMessage = this.validationMessage;
                $(this).addClass('is-invalid');
                const fieldRangeError = $(this).attr('data-range-error');
                if ((this.validity.rangeOverflow || this.validity.rangeUnderflow)
                    && fieldRangeError) {
                    validationMessage = fieldRangeError;
                }
                if (this.type === 'date') {
                    if (this.validity.rangeUnderflow) {
                        validationMessage = getTranslation('error.message.date.range.underflow')
                            .replace('{min}', $(this).attr('min'));
                    }
                    if (this.validity.rangeOverflow) {
                        validationMessage = getTranslation('error.message.date.range.overflow')
                            .replace('{max}', $(this).attr('max'));
                    }
                }
                if ((this.validity.tooLong || this.validity.tooShort)
                    && fieldRangeError) {
                    validationMessage = fieldRangeError;
                }
                if (this.validity.patternMismatch && $(this).attr('data-pattern-mismatch')) {
                    validationMessage = $(this).attr('data-pattern-mismatch');
                    const patternExample = $(this).attr('data-pattern-example');
                    if (patternExample) {
                        validationMessage += `<br>${getTranslation('validation.pattern.example')} ${patternExample}`;
                    }
                }
                if (this.validity.valueMissing && $(this).attr('data-missing-error')) {
                    validationMessage = $(this).attr('data-missing-error');
                }
                if (this.validity.badInput && $(this).attr('data-value-error')) {
                    validationMessage = $(this).attr('data-value-error');
                }
                $(this).parents('.form-group, .form-floating').find('.invalid-feedback')
                    .html(validationMessage);
            }
        });
    },
    initZipCodeValidation: function () {
        try {
            const $zipCodeFields = $('[data-validate-zip-country-target]');

            $zipCodeFields.each(function () {
                const $zipCodeField = $(this);

                const form = $zipCodeField.closest('form');
                if (!form) {
                    return;
                }
                const zipCodeRules = form.attr('data-zip-validation-rules') ? JSON.parse(form.attr('data-zip-validation-rules')) : '';

                if (!Array.isArray(zipCodeRules)) {
                    return;
                }

                const countryFieldTarget = $zipCodeField.attr('data-validate-zip-country-target');
                const $countryField = $(`${countryFieldTarget}`);
                const zipCodeRuleDefault = zipCodeRules.filter(rule => rule.countryCode === $countryField.val())[0];

                $zipCodeField.attr('pattern', zipCodeRuleDefault?.postalCode);
                $zipCodeField.attr('data-pattern-example', zipCodeRuleDefault?.patternExample);
                handleZipCodeFieldUppercase($zipCodeField, zipCodeRuleDefault);

                $countryField.on('change', function () {
                    const zipCodeRule = zipCodeRules.filter(rule => rule.countryCode === $(this).val())[0];
                    $zipCodeField.attr('pattern', zipCodeRule?.postalCode);
                    $zipCodeField.attr('data-pattern-example', zipCodeRule?.patternExample);
                    handleZipCodeFieldUppercase($zipCodeField, zipCodeRule);
                    validateField($zipCodeField[0]);
                });

                $zipCodeField.on('input blur', function () {
                    if ($zipCodeField.attr(ZIPCODE_ATTRIBUTE_IS_UPPERCASE) === 'true') {
                        transformToUpperCaseAndKeepCursor($zipCodeField);
                    }
                    validateField(this);
                });
            });
        } catch (err) {
            console.error(err);
        }
    },
    initFieldValidationEvents: function () {
        try {
            $(`input:not(${getValidationIgnoreInputTypesSelector()}), textarea`).each(function () {
                const $field = $(this);
                $field.on('input blur', function (e) {
                    if (e.type === 'blur' && e.relatedTarget && $(e.relatedTarget).closest('#lightboxModal').length) {
                        return;
                    }
                    validateField(this);
                });
            });

            $('select').on('change', function () {
                validateField(this);
            });
        } catch (err) {
            console.error(err);
        }
    },
    submit: function () {
        // idea
        // "capture: true" ensures this submit event (form validation) will be executed 1st, before other submit events (ajax call)
        // note: if validateForm() false, with stopImmediatePropagation() it will stop other submits from happening => no ajax call
        const options = {
            capture: true
        };
        document.querySelectorAll('form').forEach(form => {
            form.addEventListener('submit', function (e) {
                validateForm.call(form, e);
            }, options);
        });
    },
    validateField: validateField,
    validateForm: function (form, excludeSelector) {
        if (typeof form === 'undefined') {
            return false;
        }

        return validateForm.call(form, null, excludeSelector);
    },
    scrollToFirstInvalidField: function (form) {
        scrollToFirstInvalidField(form);
    },
    buttonClick: function () {
        $('form button[type="submit"], form input[type="submit"]').on('click', function () {
            // clear all errors when trying to submit the form
            clearForm($(this).parents('form'));
        });
    },

    functions: {
        validateForm: function (form, event) {
            validateForm.call($(form), event || null);
        },
        clearForm: clearForm
    }
};
